import { Check, Clock, Repeat2, ShoppingCart } from "lucide-react";
import { Card, CardBody } from "@flint/ui";
import { paiseToInrShort } from "../../../lib/format.ts";
import type React from "react";
import type { BillListKpis } from "./types.ts";

export interface BillKpiStripProps {
  kpis: BillListKpis;
}

export function BillKpiStrip({ kpis }: BillKpiStripProps) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <Tile
        accent="sky"
        icon={<ShoppingCart size={15} />}
        label="Outstanding"
        value={paiseToInrShort(kpis.outstandingPaise)}
        meta={`${kpis.outstandingBillCount} open bills · ${kpis.outstandingVendorCount} vendors`}
      />
      <Tile
        accent="amber"
        icon={<Clock size={15} />}
        label="Awaiting approval"
        value={paiseToInrShort(kpis.awaitingApprovalPaise)}
        meta={`${kpis.approvalReadyCount} ready · ${kpis.heldForProofCount} held for proof`}
      />
      <Tile
        accent="emerald"
        icon={<Check size={15} />}
        label="Paid · this month"
        value={paiseToInrShort(kpis.paidThisMonthPaise)}
        meta={`${kpis.paidThisMonthCount} bill${kpis.paidThisMonthCount === 1 ? "" : "s"}`}
      />
      <Tile
        accent="muted"
        icon={<Repeat2 size={15} />}
        label="Recurring"
        value={`${kpis.recurringVendorCount}`}
        unit="vendors"
        meta={`${paiseToInrShort(kpis.recurringMonthlyPaise)}/mo · utilities + telecom`}
      />
    </div>
  );
}

type TileAccent = "sky" | "amber" | "emerald" | "muted";

interface TileProps {
  accent: TileAccent;
  icon: React.ReactNode;
  label: string;
  value: string;
  meta: string;
  unit?: string;
}

const ACCENT_BAR: Record<TileAccent, string> = {
  sky: "bg-sky-500",
  amber: "bg-amber-500",
  emerald: "bg-emerald-500",
  muted: "bg-line-2",
};

const ICON_TONE: Record<TileAccent, string> = {
  sky: "bg-sky-50 text-sky-700",
  amber: "bg-amber-50 text-amber-700",
  emerald: "bg-emerald-50 text-emerald-700",
  muted: "bg-raised text-muted",
};

function Tile({ accent, icon, label, value, meta, unit }: TileProps) {
  return (
    <Card>
      <CardBody className="flex items-stretch gap-3 p-4">
        <span aria-hidden className={`w-1 shrink-0 rounded-sm ${ACCENT_BAR[accent]}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="truncate text-sm font-medium text-muted">{label}</p>
            <span
              aria-hidden
              className={`grid h-7 w-7 shrink-0 place-items-center rounded-md ${ICON_TONE[accent]}`}
            >
              {icon}
            </span>
          </div>
          <p className="mt-1 truncate text-xl font-semibold text-ink">
            {value}
            {unit ? (
              <>
                {" "}
                <span className="text-sm font-medium text-muted">{unit}</span>
              </>
            ) : null}
          </p>
          <p className="mt-1 truncate text-sm text-muted">{meta}</p>
        </div>
      </CardBody>
    </Card>
  );
}
